import { z } from "zod";

/**
 * The filter AST. The filter bar, saved views, smart playlists, and board columns
 * all compile through this one path. Phase 6 builds the compiler; this is the
 * contract both ends agree on.
 */

export const CMP_OPS = [
  "eq",
  "neq",
  "contains",
  "starts",
  "ends",
  "gt",
  "gte",
  "lt",
  "lte",
  "between",
  "in",
  "nin",
  "empty",
  "notempty",
  /** Relative dates, for follow-ups. */
  "within_days",
  "before_days",
] as const;

export const CmpSchema = z.enum(CMP_OPS);
export type Cmp = z.infer<typeof CmpSchema>;

/** A bare scalar a comparison can hold. */
export const FilterScalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const FilterValueSchema = z.union([
  FilterScalarSchema,
  z.array(FilterScalarSchema).max(1000),
]);

export type FilterValue = z.infer<typeof FilterValueSchema>;

/**
 * Resolved against a whitelist registry at compile time, never interpolated into
 * SQL. The pattern only rejects obvious nonsense early; an unknown-but-well-formed
 * name still 400s in the compiler.
 */
export const FilterFieldSchema = z
  .string()
  .regex(/^[a-z_]+\.[a-z0-9_-]+$/, "Expected a namespaced field, e.g. track.name or field.script");

export const FilterLeafSchema = z.object({
  field: FilterFieldSchema,
  cmp: CmpSchema,
  value: FilterValueSchema.optional(),
});

export type FilterLeaf = z.infer<typeof FilterLeafSchema>;

export type Filter =
  | { op: "and"; nodes: Filter[] }
  | { op: "or"; nodes: Filter[] }
  | { op: "not"; node: Filter }
  | FilterLeaf;

/** Caps so a malformed saved view cannot build a pathological query. */
export const MAX_FILTER_DEPTH = 16;
export const MAX_FILTER_NODES = 200;

export const FilterSchema: z.ZodType<Filter> = z.lazy(() =>
  z.union([
    z.object({ op: z.literal("and"), nodes: z.array(FilterSchema).max(MAX_FILTER_NODES) }),
    z.object({ op: z.literal("or"), nodes: z.array(FilterSchema).max(MAX_FILTER_NODES) }),
    z.object({ op: z.literal("not"), node: FilterSchema }),
    FilterLeafSchema,
  ]),
);

function measure(node: Filter, depth: number): { depth: number; count: number } {
  if (depth > MAX_FILTER_DEPTH) return { depth, count: Infinity };

  if ("op" in node) {
    const children = node.op === "not" ? [node.node] : node.nodes;
    let maxDepth = depth;
    let count = 1;
    for (const child of children) {
      const m = measure(child, depth + 1);
      maxDepth = Math.max(maxDepth, m.depth);
      count += m.count;
    }
    return { depth: maxDepth, count };
  }

  return { depth, count: 1 };
}

/**
 * Depth and total-node caps, checked after parsing because Zod's `max` only sees
 * one level at a time.
 */
export const BoundedFilterSchema = FilterSchema.superRefine((node, ctx) => {
  const { depth, count } = measure(node, 1);
  if (depth > MAX_FILTER_DEPTH) {
    ctx.addIssue({ code: "custom", message: `Filter nests deeper than ${MAX_FILTER_DEPTH}` });
  }
  if (count > MAX_FILTER_NODES) {
    ctx.addIssue({ code: "custom", message: `Filter has more than ${MAX_FILTER_NODES} nodes` });
  }
});

export const SORT_DIRECTIONS = ["asc", "desc"] as const;

export const SortSchema = z.object({
  field: FilterFieldSchema,
  dir: z.enum(SORT_DIRECTIONS).default("asc"),
});

export type Sort = z.infer<typeof SortSchema>;

export const ColumnStateSchema = z.object({
  field: FilterFieldSchema,
  width: z.number().int().min(40).max(1200).optional(),
  hidden: z.boolean().optional(),
});

export type ColumnState = z.infer<typeof ColumnStateSchema>;
